import Hls from 'hls.js';

export function createMuxStreamSourceFromPublicVideoIdentifier(publicVideoIdentifier) {
  return `https://stream.mux.com/${encodeURIComponent(publicVideoIdentifier)}.m3u8`;
}

/** First-frame still; same playback id as stream.mux.com. Width capped so the JPEG is not far sharper than typical H.264 ABR playback (Mux stills default to high resolution; HLS is compressed). */
export function createMuxPosterImageUrlFromPublicVideoIdentifier(publicVideoIdentifier) {
  const trimmed = (publicVideoIdentifier || '').trim();
  if (!trimmed) return '';
  const query = new URLSearchParams({
    time: '0',
    width: '960',
    fit_mode: 'preserve'
  });
  return `https://image.mux.com/${encodeURIComponent(trimmed)}/thumbnail.jpg?${query.toString()}`;
}

export function createChannelsFromMuxIds({ muxIds, muxMeta = {}, maxChannels = 100 } = {}) {
  const validIds = (muxIds || [])
    .map(publicVideoIdentifier => (publicVideoIdentifier || '').trim())
    .filter(Boolean)
    .slice(0, maxChannels);

  return validIds.map((publicVideoIdentifier, channelIndex) => {
    const meta = muxMeta[publicVideoIdentifier];
    const title = meta && meta.title ? meta.title : `Mux ${publicVideoIdentifier}`;
    return {
      name: `CH${channelIndex + 1}`,
      src: createMuxStreamSourceFromPublicVideoIdentifier(publicVideoIdentifier),
      muxPublicVideoIdentifier: publicVideoIdentifier,
      posterUrl: createMuxPosterImageUrlFromPublicVideoIdentifier(publicVideoIdentifier),
      file: `Mux ${publicVideoIdentifier}`,
      title
    };
  });
}

export function ensurePlaceholderChannels(channels, { placeholderCount = 3 } = {}) {
  const next = Array.isArray(channels) ? channels.slice() : [];
  if (next.filter(Boolean).length === 0) {
    for (let i = 0; i < placeholderCount; i++) {
      next[i] = { name: `CH${i + 1}`, src: null };
    }
  }
  return next.filter(Boolean);
}

function shouldUseHttpLiveStreamingLibrary(channel) {
  return Boolean(channel.muxPublicVideoIdentifier) && Hls.isSupported();
}

function browserCanPlayNativeHttpLiveStreaming(videoElement) {
  return videoElement.canPlayType('application/vnd.apple.mpegurl') !== '';
}

function playVideoElement(videoElement) {
  const playPromise = videoElement.play();
  if (playPromise && typeof playPromise.catch === 'function') {
    playPromise.catch(() => {});
  }
}

function resolveChannelPosterUrlForElement(channel) {
  return (
    (channel.posterUrl && String(channel.posterUrl).trim()) ||
    (channel.muxPublicVideoIdentifier
      ? createMuxPosterImageUrlFromPublicVideoIdentifier(channel.muxPublicVideoIdentifier)
      : '')
  );
}

function detachChannelMedia(channel) {
  if (!channel || !channel.el || channel.el.tagName !== 'VIDEO') return;

  if (channel.hlsInstance && typeof channel.hlsInstance.destroy === 'function') {
    channel.hlsInstance.destroy();
    channel.hlsInstance = null;
  }

  channel.el.pause();
  channel.el.removeAttribute('src');
  channel.el.removeAttribute('poster');
  channel.el.load?.();
}

function attachChannelMedia(channel, { startPlayback = false, restartPlaybackFromBeginning = false } = {}) {
  if (!channel || !channel.src || !channel.el || channel.el.tagName !== 'VIDEO') return;

  detachChannelMedia(channel);

  const videoElement = channel.el;
  const posterUrl = resolveChannelPosterUrlForElement(channel);
  if (posterUrl) videoElement.poster = posterUrl;

  const attachmentGeneration = (channel.mediaAttachmentGeneration || 0) + 1;
  channel.mediaAttachmentGeneration = attachmentGeneration;

  const playWhenReady = () => {
    if (channel.mediaAttachmentGeneration !== attachmentGeneration) return;
    if (restartPlaybackFromBeginning) {
      try {
        videoElement.currentTime = 0;
      } catch {
        /* ignore seek errors on unloaded media */
      }
    }
    if (startPlayback) {
      playVideoElement(videoElement);
    }
  };

  if (shouldUseHttpLiveStreamingLibrary(channel)) {
    const hlsInstance = new Hls({
      enableWorker: true,
      // Mux now serves separate audio renditions on edgemv hosts.
      startLevel: -1
    });
    hlsInstance.on(Hls.Events.MANIFEST_PARSED, playWhenReady);
    hlsInstance.on(Hls.Events.ERROR, (_event, data) => {
      if (!data?.fatal) return;
      if (channel.mediaAttachmentGeneration !== attachmentGeneration) return;
      console.warn('Mux HLS playback failed', data);
    });
    hlsInstance.loadSource(channel.src);
    hlsInstance.attachMedia(videoElement);
    channel.hlsInstance = hlsInstance;
    return;
  }

  if (browserCanPlayNativeHttpLiveStreaming(videoElement)) {
    videoElement.addEventListener('loadedmetadata', playWhenReady, { once: true });
    videoElement.src = channel.src;
    return;
  }

  console.warn('No HLS playback path available for Mux video');
}

export function buildChannelElements({ channels, screenElement, isMuted, onVideoEnded }) {
  if (!screenElement) return;

  channels.forEach((channel, channelIndex) => {
    if (!channel) return;

    if (channel.src) {
      const videoElement = document.createElement('video');
      videoElement.loop = false;
      videoElement.muted = isMuted;
      videoElement.playsInline = true;
      videoElement.className = 'channel-video';
      videoElement.preload = 'metadata';

      if (typeof onVideoEnded === 'function') {
        videoElement.addEventListener('ended', onVideoEnded);
        channel.onVideoEndedHandler = onVideoEnded;
      }

      screenElement.insertBefore(videoElement, screenElement.firstChild);
      channel.el = videoElement;
      return;
    }

    // Placeholder: colored canvas
    const placeholderCanvasElement = document.createElement('canvas');
    placeholderCanvasElement.width = 640;
    placeholderCanvasElement.height = 360;
    placeholderCanvasElement.className = 'channel-video';
    const colors = ['#0d1a0d', '#0d0d1a', '#1a0d0d'];
    const canvasContext = placeholderCanvasElement.getContext('2d');
    canvasContext.fillStyle = colors[channelIndex % colors.length];
    canvasContext.fillRect(0, 0, 640, 360);
    canvasContext.fillStyle = 'rgba(0,255,136,0.3)';
    canvasContext.font = 'bold 64px monospace';
    canvasContext.textAlign = 'center';
    canvasContext.fillText(`CH ${channelIndex + 1}`, 320, 200);
    canvasContext.font = '20px monospace';
    canvasContext.fillStyle = 'rgba(255,255,255,0.2)';
    canvasContext.fillText('NO SIGNAL', 320, 250);
    screenElement.insertBefore(placeholderCanvasElement, screenElement.firstChild);
    channel.el = placeholderCanvasElement;
  });
}

export function setMuteOnChannels(channels, isMuted) {
  channels.forEach(channel => {
    if (channel && channel.el && channel.el.tagName === 'VIDEO') channel.el.muted = isMuted;
  });
}

export function switchChannel({
  channels,
  state,
  targetChannelIndex,
  animate = true,
  restartPlaybackFromBeginning = false,
  startPlayback = true,
  showStatic,
  updateOsd,
  updateDots,
  updateRemoteDisplay,
  updatePlayPauseBtn,
  onSwitched
}) {
  if (targetChannelIndex < 0 || targetChannelIndex >= channels.length) return;

  const performChannelSwitch = () => {
    const previousChannel = channels[state.currentChannelIndex];
    if (previousChannel && previousChannel.el) {
      previousChannel.el.classList.remove('active');
      if (previousChannel.el.tagName === 'VIDEO') detachChannelMedia(previousChannel);
    }

    state.currentChannelIndex = targetChannelIndex;
    state.isPaused = !startPlayback;

    const activeChannel = channels[state.currentChannelIndex];
    if (activeChannel && activeChannel.el) {
      activeChannel.el.classList.add('active');
      if (activeChannel.el.tagName === 'VIDEO') {
        activeChannel.el.muted = state.isMuted;
        attachChannelMedia(activeChannel, { startPlayback, restartPlaybackFromBeginning });
      }
    }

    updateOsd?.();
    updateDots?.();
    updateRemoteDisplay?.();
    updatePlayPauseBtn?.();
    onSwitched?.(state.currentChannelIndex);
  };

  if (animate && typeof showStatic === 'function') {
    const previousChannel = channels[state.currentChannelIndex];
    if (previousChannel && previousChannel.el) {
      previousChannel.el.classList.remove('active');
      if (previousChannel.el.tagName === 'VIDEO') detachChannelMedia(previousChannel);
    }
    showStatic(400, performChannelSwitch);
  } else {
    performChannelSwitch();
  }
}

export function destroyChannels(channels) {
  channels.forEach(channel => {
    if (!channel) return;
    detachChannelMedia(channel);
    if (channel.el && channel.el.tagName === 'VIDEO') {
      if (channel.onVideoEndedHandler) {
        channel.el.removeEventListener('ended', channel.onVideoEndedHandler);
        channel.onVideoEndedHandler = null;
      }
      channel.el.removeAttribute('poster');
    }
  });
}

